import SupUiLoading from "@/components/Ui/Loading";
import Web3CommonSwitchNetwork from "@/components/Web3/Common/SwitchNetwork";
import Web3TransmuteFinished from "@/components/Web3/Transmute/Finished";
import type { SupWeb3VaultBalance } from "@/hooks/api/useTokenSuperpositionsBalances";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import React, { Fragment, type PropsWithChildren, useMemo, useState } from "react";

import Web3TransmuteRegisterTx from "@/components/Web3/Transmute/RegisterTx";
import UiChipVault from "@/components/Ui/Chip/Vault";

import useErc20VaultsCheck from "@/hooks/api/erc20/useErc20VaultsCheck";
import { SupAssetLabel, SupButton, SupText } from "@repo/core-ui";
import { SupMint } from "@repo/core-ui";
import dictionaryConfig from "@/../config/dictionary.config";
import Link from "next/link";
import UiLabelChainScanLink from "@/components/Ui/Label/ChainScanLink";

interface Web3TransmuteRegisterVaultProps extends PropsWithChildren {
  superPosition: SupWeb3VaultBalance;
  handleCancel?: () => void;
}

export default function Web3TransmuteRegisterVault({ handleCancel, superPosition, children }: Web3TransmuteRegisterVaultProps) {
  const [isRegistered, setIsRegistered] = useState<boolean>(false);
  const [txHash, setTxHash] = useState<string>();

  const { network } = useDynamicContext();

  const { data, isLoading } = useErc20VaultsCheck(isRegistered ? "default" : "cached");

  const requireRegistration = useMemo(() => {
    return data?.find(vault => vault.superform_id === superPosition.vault.superform_id)?.has_aerc_20_registered === false;
  }, [data, superPosition.vault.superform_id]);

  const requireSwitchChain = useMemo(() => {
    return network ? parseInt(`${network}`, 10) !== superPosition.vault.chain.id : false;
  }, [network, superPosition.vault.chain.id]);

  const handleOnFinished = (txHash: string) => {
    setIsRegistered(true);
    setTxHash(txHash);
  };

  const extraContent = useMemo(
    () =>
      txHash && (
        <div className="-mx-2">
          <UiLabelChainScanLink address={txHash} chain={Number(superPosition.vault.chain.id)} />
        </div>
      ),
    [txHash, superPosition.vault.chain.id]
  );

  return isLoading || !data ? (
    <SupUiLoading />
  ) : requireRegistration ? (
    isRegistered ? (
      <Web3TransmuteFinished
        description="Please wait 60 minutes for the registration process to complete."
        showLink
        handleClose={handleCancel}
        title="Vault registered"
        extraContent={extraContent}
      />
    ) : (
      <Fragment>
        <div className="space-y-4">
          <div className="inline-flex h-fit w-full flex-col items-start justify-start space-y-4 border-[.5px] border-neutral-100 bg-neutral-25 p-4">
            <div className="flex w-full items-center justify-between">
              <SupAssetLabel
                className="w-fit items-center gap-2"
                label={
                  <SupText variant="caption" weight="bold" className="text-neutral-700">
                    Register Vault
                  </SupText>
                }
              >
                <SupMint className="h-4 w-4 text-neutral-900" />
              </SupAssetLabel>
            </div>
            <div className="flex h-14 flex-col items-start justify-start gap-1 self-stretch">
              <div className="inline-flex items-center justify-between self-stretch py-1.5">
                <UiChipVault singleVault={superPosition.vault} chainId={superPosition.chain_id} />
              </div>
            </div>
            <SupText variant="caption" className="space-y-3 self-stretch normal-case text-neutral-700" as="div">
              <p>Transmuting allows you to convert your ERC-1155 SuperPosition into an ERC-20 SuperPosition (or the other way around).</p>

              <p>You can transmute in either direction at any time.</p>

              <p>If you are intending to deposit your position into a SuperPool on Uniswap, Curve, or Balancer, you will need to transmute to ERC-20.</p>

              <p className="text-[#D54A2A]">
                Because this is the first time this vault has been transmuted, it will need to be registered first. After calling register please wait 60 minutes to ensure the
                vault has registered across all chains.
              </p>
            </SupText>

            <Link href={dictionaryConfig.superPools.registerHelpArticle} className="text-neutral-700 underline hover:no-underline" target="_blank">
              <SupText variant="caption" weight="bold" className="self-stretch">
                Learn More
              </SupText>
            </Link>
          </div>
        </div>
        <div className="mt-auto flex w-full flex-col items-stretch justify-between space-y-4">
          <SupButton fluid onClick={handleCancel} className="hover:opacity-80" variant="outlined" color="primary">
            Cancel
          </SupButton>

          {requireSwitchChain ? (
            <Web3CommonSwitchNetwork targetChainId={superPosition.vault.chain.id} />
          ) : (
            <Web3TransmuteRegisterTx superPosition={superPosition} onFinished={handleOnFinished} />
          )}
        </div>
      </Fragment>
    )
  ) : (
    children
  );
}
